import { z } from 'zod';
import { timestampSchema } from './common.schemas';

/**
 * Authentication & Authorization Schemas
 */

// Login
export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

export const loginResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  user: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    role: z.enum(['admin', 'agent', 'viewer']),
    organizationId: z.string().uuid(),
  }),
});

// Register
export const registerSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(100),
  name: z.string().min(1).max(100),
  organizationName: z.string().min(1).max(100),
});

export const registerResponseSchema = loginResponseSchema;

// Refresh Token
export const refreshTokenSchema = z.object({
  refreshToken: z.string(),
});

export const refreshTokenResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number(),
});

// API Key Creation
export const createApiKeySchema = z.object({
  name: z.string().min(1).max(100),
  permissions: z.array(z.string()).min(1),
  expiresAt: z.coerce.date().optional(),
});

export const apiKeyResponseSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    key: z.string(), // Only returned on creation
    keyPrefix: z.string(),
    permissions: z.array(z.string()),
    expiresAt: z.coerce.date().nullable(),
    lastUsedAt: z.coerce.date().nullable(),
  })
  .merge(timestampSchema);

export const apiKeyListItemSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    keyPrefix: z.string(),
    permissions: z.array(z.string()),
    expiresAt: z.coerce.date().nullable(),
    lastUsedAt: z.coerce.date().nullable(),
  })
  .merge(timestampSchema);

// Change Password
export const changePasswordSchema = z.object({
  currentPassword: z.string(),
  newPassword: z.string().min(8).max(100),
});

// Export inferred types
export type LoginInput = z.infer<typeof loginSchema>;
export type LoginResponse = z.infer<typeof loginResponseSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type RegisterResponse = z.infer<typeof registerResponseSchema>;
export type RefreshTokenInput = z.infer<typeof refreshTokenSchema>;
export type RefreshTokenResponse = z.infer<typeof refreshTokenResponseSchema>;
export type CreateApiKeyInput = z.infer<typeof createApiKeySchema>;
export type ApiKeyResponse = z.infer<typeof apiKeyResponseSchema>;
export type ApiKeyListItem = z.infer<typeof apiKeyListItemSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
